<script>
  import { tick } from 'svelte';
  import { derived, get } from "svelte/store";
  import MenuBar from "../../../components/menuBar/MenuBar.svelte";
  import MenuBarItem from "../../../components/menuBar/MenuBarItem.svelte";
  import GeneralSelect from "../../../components/select/GeneralSelect.svelte";
  import GeneralButton from "../../../components/button/GeneralButton.svelte";
  import { translate } from "../../../i18n/i18next.js";
  import ToggleSwitch from "../../../components/input/ToggleSwitch.svelte";
  import DisplayColumnsSettings from "../../../components/ganttchart/DisplayColumnsSettings.svelte";
  import GeneralPanelButton from "../../../components/button/GeneralPanelButton.svelte";
  import MenuBarMenuDialog from "../../../components/menuBar/menuDialog/MenuBarMenuDialog.svelte";
  import MenuBarMenuDialogButton from "../../../components/menuBar/menuDialog/MenuBarMenuDialogButton.svelte";
  import OrderGanttDisplaySettingsMenuDialog from "./OrderGanttDisplaySettingsMenuDialog.svelte";
  import OrderGanttFilterMenuDialog from "./OrderGanttFilterMenuDialog.svelte";
  import SimpleOrderGanttFilterMenuDialog from "../SimpleOrderGantt/SimpleOrderGanttFilterMenuDialog.svelte";
  import GanttHelper from '../../../components/ganttchart/GanttHelper';
  import { useEnabledExperimentalFeatures } from "../../../hooks/useEnabledExperimentalFeatures.js"
  import OrderGanttAdminMenuDialog from './OrderGanttAdminMenuDialog.svelte';
  

  export let GanttStore

  const {
    CurrentDisplayedEndDate,
    CurrentStyle,
    DisplayedStartDate,
    GanttChartType,
    StyleObjects,
    ValidEndDate,
    ValidStartDate,
  } = GanttStore

  let applyResult = false;

  let settingsDialogVisible = false;

  let columnSettingsDialog = null
  let isColumnSettingsDialogShown = false

  async function openColumnSettings() {
    isColumnSettingsDialogShown = true
    await tick()
    columnSettingsDialog.showModal()
  }

  function closeColumnSettings() {
    columnSettingsDialog.close()
    isColumnSettingsDialogShown = false
  }


  function onChangeSettings(newValue) {
    GanttStore.ApplyResult.set(newValue);
  }

  let dialogCurrentlyDisplayed = null
  async function onClickSettingsDialogOpen() {
    dialogCurrentlyDisplayed = "menuSelection"
  }
  function onClickSettingsDialogClose() {
    dialogCurrentlyDisplayed = null
  }

  const MoveButton_Home = () => {
    GanttStore.CurrentDisplayedStartDate.update((_) => new Date($DisplayedStartDate));
  }

  const MoveButton_PrevPage = () => {
    const oldDate = get(GanttStore.CurrentDisplayedStartDate)
    const newDate = new Date(Math.max($ValidStartDate.getTime(), oldDate.getTime() - ($CurrentDisplayedEndDate.getTime() - oldDate.getTime())))
    GanttStore.CurrentDisplayedStartDate.set(newDate);
  }

  const MoveButton_Prev = () => {
    const oldDate = get(GanttStore.CurrentDisplayedStartDate)
    const newDate = new Date(Math.max($ValidStartDate.getTime(), oldDate.getTime() - GanttHelper.GHDAY * 1000))
    GanttStore.CurrentDisplayedStartDate.set(newDate);
  }

  const MoveButton_Today = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (today > $ValidEndDate || today < $ValidStartDate) return;
    GanttStore.CurrentDisplayedStartDate.set(today)
  }

  const MoveButton_Next = () => {
    const oldDate = get(GanttStore.CurrentDisplayedStartDate)
    const span = get(GanttStore.DisplayedSpanInSeconds);
    const newDate = new Date(Math.min($ValidEndDate.getTime() - span * 1000, oldDate.getTime() + GanttHelper.GHDAY * 1000))
    if (newDate > oldDate) GanttStore.CurrentDisplayedStartDate.set(newDate)
  }

  const MoveButton_NextPage = () => {
    const span = get(GanttStore.DisplayedSpanInSeconds);
    const newDate = new Date(Math.min($ValidEndDate.getTime() - span * 1000, $CurrentDisplayedEndDate.getTime()))
    GanttStore.CurrentDisplayedStartDate.set(newDate)
  }

  const experimentalFeatures = useEnabledExperimentalFeatures();
</script>

<MenuBar onClickDetailedMenuOpen={onClickSettingsDialogOpen}>
  <svelte:fragment slot="settings">
    <MenuBarItem>
      <GeneralSelect
        selectId="gantt_upperpane_styleselect"
        value={$CurrentStyle.ObjectID}
        options={$StyleObjects.map((style)=>({text: style.StyleName, value: style.ObjectID}))}
        onChangeEvent={e => CurrentStyle.set($StyleObjects.find((o) => o.ObjectID == e.target.value))}
      />
    </MenuBarItem>
    {#if $GanttChartType === GanttStore.GanttChartTypes.SimpleOrderGantt
      || $GanttChartType === GanttStore.GanttChartTypes.ExpectedActualCmp}
      <MenuBarItem>
        <GeneralButton
          buttonText={translate("frontend:ganttChart.columnSettings.label")}
          buttonType="secondary"
          buttonFunction={openColumnSettings}
          buttonExtraStyle="white-space: nowrap"
        />
      </MenuBarItem>
    {/if}

    {#if $experimentalFeatures.includes("applyResult") && ($GanttChartType === GanttStore.GanttChartTypes.OrderGantt
      || $GanttChartType === GanttStore.GanttChartTypes.SimpleOrderGantt)}
      <ToggleSwitch
        bind:on={applyResult}
        label={translate("frontend:ganttChart.settings.applyResult")}
        onChange={e => onChangeSettings(e.target.checked)}
      />
    {/if}

    <MenuBarItem>
      {#if isColumnSettingsDialogShown}
        <DisplayColumnsSettings
          GanttStore={GanttStore}
          bind:dialog={columnSettingsDialog}
          ganttChartType={$GanttChartType}
          on:close={closeColumnSettings}
        />
      {/if}
    </MenuBarItem>
  </svelte:fragment>
  <GeneralPanelButton
    slot="pagination"
    items={[
      {
        buttonText: translate("generic.startShort"),
        buttonFunction: MoveButton_Home,
      },
      {
        buttonImage: '/assets/Pager_FL.svg',
        buttonFunction: MoveButton_PrevPage,
      },
      {
        buttonImage: '/assets/Pager_L.svg',
        buttonFunction: MoveButton_Prev,
      },
      {
        buttonText: translate("generic.today"),
        buttonFunction: MoveButton_Today,
      },
      {
        buttonImage: '/assets/Pager_R.svg',
        buttonFunction: MoveButton_Next,
      },
      {
        buttonImage: '/assets/Pager_FR.svg',
        buttonFunction: MoveButton_NextPage,
      },
    ]}
  />
</MenuBar>
{#if dialogCurrentlyDisplayed === "menuSelection"}
  <MenuBarMenuDialog onClose={onClickSettingsDialogClose}>
    <MenuBarMenuDialogButton onClick={() => dialogCurrentlyDisplayed = "displaySettings"}>
      {translate("frontend:ganttChart.settings.sections.displaySettings.title")}
    </MenuBarMenuDialogButton>
    <MenuBarMenuDialogButton onClick={() => dialogCurrentlyDisplayed = "filter"}>
      {translate("generic.filterShort")}
    </MenuBarMenuDialogButton>
    <MenuBarMenuDialogButton onClick={() => dialogCurrentlyDisplayed = "admin"}>
      {translate("frontend:generic.projectAdminSettings.title")}
    </MenuBarMenuDialogButton>
  </MenuBarMenuDialog>
{/if}
{#if dialogCurrentlyDisplayed === "displaySettings"}
  <OrderGanttDisplaySettingsMenuDialog
    GanttStore={GanttStore}
    onClose={() => dialogCurrentlyDisplayed = null}
  />
{/if}
{#if dialogCurrentlyDisplayed === "filter"}
  {#if $GanttChartType === GanttStore.GanttChartTypes.SimpleOrderGantt
    || $GanttChartType === GanttStore.GanttChartTypes.ExpectedActualCmp}
    <SimpleOrderGanttFilterMenuDialog
      GanttStore={GanttStore}
      onClose={() => dialogCurrentlyDisplayed = null}
    />
  {:else}
    <OrderGanttFilterMenuDialog
      GanttStore={GanttStore}
      onClose={() => dialogCurrentlyDisplayed = null}
    />
  {/if}
{/if}
{#if dialogCurrentlyDisplayed === "admin"}
  <OrderGanttAdminMenuDialog
    GanttStore={GanttStore}
    onClose={() => dialogCurrentlyDisplayed = null}
  />
{/if}
